import { token } from '@atlaskit/tokens';
import { css } from '@compiled/react';
import { CERTIFICATE_DEFAULT_URL } from 'constants/index';
import { trackCertificationCardClick } from 'analytics/certificationsAnalytics';
import CertificationBannerImage from '../../../../assets/images/certification-banner-image.svg';
import { Tag } from '@atlassian/learning-components';

export type CertificationData = {
  name: string | null;
  imageUrl: string | null;
  publicUrl: string | null;
  nameAbbr: string | null;
};

const Certification = ({
  data,
  isPersonalView,
  isMainPanel,
  testId,
}: {
  data: CertificationData;
  isPersonalView: boolean;
  isMainPanel?: boolean;
  testId?: string;
}) => {
  if (!data.name) {
    return null;
  }

  // add data.publicUrl back in when we have the data
  // const certificationUrl = data.publicUrl || CERTIFICATE_DEFAULT_URL;
  const certificationUrl = CERTIFICATE_DEFAULT_URL;

  const onClickCertification = () => {
    trackCertificationCardClick(
      data.name ?? 'not-named',
      isMainPanel ?? false,
      isPersonalView,
    );
    window.open(certificationUrl);
  };

  const generateTagText = (
    nameAbbr: string | null,
    name: string,
  ): string | null => {
    if (name.toLowerCase().includes('fundamentals')) {
      return 'certificate';
    }
    if (typeof nameAbbr !== 'string') {
      return null;
    }
    if (nameAbbr.startsWith('ACP')) {
      return 'professional';
    } else if (nameAbbr.startsWith('ACA')) {
      return 'associate';
    } else {
      return null;
    }
  };

  const tagText = generateTagText(data.nameAbbr, data.name);

  return (
    <button
      data-testid={testId}
      css={[
        unstyledButtonStyles,
        cardContainerStyles,
        isMainPanel && mainPanelCardStyle,
      ]}
      role="button"
      onClick={onClickCertification}
    >
      <div css={topBackgroundStyles}></div>
      <div css={cardContentStyles}>
        <div css={certificationImageContainerStyles}>
          {data.imageUrl && (
            <img
              css={certificationImageStyles}
              src={data.imageUrl}
              alt={data.name}
            />
          )}
        </div>
        {tagText && (
          <div css={tagWrapper}>
            <Tag label={tagText} />
          </div>
        )}

        <h5 css={courseNameStyles}>{data.name}</h5>
      </div>
    </button>
  );
};
const mainPanelCardStyle = css({
  '@media (min-width: 768px) and (max-width: 1279px)': {
    maxWidth: 'calc(50% - 12px)',
  },
  '@media (min-width: 1280px)': {
    flex: '0 0 266px',
  },
});

const unstyledButtonStyles = css({
  display: 'flex',
  textAlign: 'inherit',
  textDecoration: 'none',
  background: 'none',
  color: 'inherit',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  fontFamily: token('font.family.brand.body'),
});

const tagWrapper = css({
  marginTop: '8px',
  marginBottom: '-6px',
  display: 'inline-flex',
});

const cardContainerStyles = css({
  position: 'relative',
  textDecoration: 'none',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  boxShadow: token('elevation.shadow.raised'),
  borderRadius: token('space.100'),
  background: token('elevation.surface'),
  zIndex: 1,
  height: '266px',
  flex: '1 1 100%',
  transition: 'box-shadow 0.2s ease',
  '@media (min-width: 768px) and (max-width: 1279px)': {
    minWidth: '230px',
    flex: '1 1 calc(50% - 12px)',
  },
  '@media (min-width: 1280px)': {
    width: '268px',
  },
  '&:hover': {
    boxShadow: token('elevation.shadow.overlay'),
  },
  '&:active': {
    outline: `2px solid ${token('color.border.selected')}`,
  },
});

const topBackgroundStyles = css({
  position: 'absolute',
  backgroundImage: `url(${CertificationBannerImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  borderRadius: `${token('space.100')} ${token('space.100')} 0 0`,
  top: '0',
  left: '0',
  height: '68px',
  width: '100%',
  zIndex: 2,
});

const cardContentStyles = css({
  display: 'flex',
  flexDirection: 'column',
  padding: token('space.300'),
  zIndex: 4,
});

const courseNameStyles = css({
  color: token('color.text'),
  marginTop: '14px',
  fontFamily: token('font.family.brand.heading'),
  fontSize: '20px',
  fontWeight: token('font.weight.semibold'),
  lineHeight: '28px',
  display: '-webkit-box',
  '-webkit-box-orient': 'vertical',
  '-webkit-line-clamp': '3',
  overflow: 'hidden',
});

const certificationImageContainerStyles = css({
  borderRadius: '50%',
  height: '90px',
  width: '90px',
  overflow: 'hidden',
});

const certificationImageStyles = css({
  width: '100%',
  height: '100%',
});

export default Certification;
